<template>
  <div
    class="ruler-picker"
    :class="'is-' + direction"
    @touchstart.passive="onStart"
    @touchmove.prevent="onMove"
    @touchend="onEnd"
    @mousedown="onMouseDown"
  >
    <div class="ruler-track" :style="trackStyle">
      <div
        v-for="tick in ticks"
        :key="tick.value"
        class="ruler-tick"
        :class="tickClass(tick)"
        :style="tickPos(tick)"
      >
        <span v-if="tick.major" class="ruler-label">{{ formatLabel(tick.value) }}</span>
      </div>
    </div>
    <div class="ruler-indicator" />
  </div>
</template>

<script>
/**
 * 标尺选择器
 * - vertical：身高上下滑
 * - horizontal：按设计稿左右滑（刻度在上、数字在下、居中指示条）
 */
export default {
  name: 'RulerPicker',
  props: {
    /** 当前值 */
    value: { type: Number, required: true },
    /** 最小值 */
    min: { type: Number, default: 0 },
    /** 最大值 */
    max: { type: Number, default: 100 },
    /** 步长 */
    step: { type: Number, default: 1 },
    /** horizontal | vertical */
    direction: { type: String, default: 'horizontal' },
    /** 主刻度间隔（显示数字） */
    majorEvery: { type: Number, default: 10 },
    /** 像素/单位 */
    unitSize: { type: Number, default: 8 }
  },
  
  data() {
    return {
      dragging: false,
      startPos: 0,
      startValue: 0
    }
  },
  computed: {
    /**
     * @returns {boolean}
     */
    isHorizontal() {
      return this.direction === 'horizontal'
    },
    /**
     * @returns {{ value: number, major: boolean, mid: boolean }[]}
     */
    ticks() {
      const list = []
      const precision = this.step < 1 ? 1 : 0
      const midEvery = this.step < 1 ? 0.5 : Math.max(this.majorEvery / 2, this.step)
      for (let v = this.min; v <= this.max + 1e-8; v = Number((v + this.step).toFixed(4))) {
        const rounded = Number(v.toFixed(precision || 4))
        const major = this.isMultiple(rounded, this.majorEvery)
        const mid = !major && this.isMultiple(rounded, midEvery)
        list.push({ value: rounded, major, mid })
      }
      return list
    },
    trackStyle() {
      const offset = (this.value - this.min) * this.unitSize
      if (this.isHorizontal) {
        return { transform: `translateX(${-offset}px)` }
      }
      return { transform: `translateY(${-offset}px)` }
    }
  },
  methods: {
    /**
     * @param {number} value
     * @param {number} every
     * @returns {boolean}
     */
    isMultiple(value, every) {
      if (!every) return false
      const q = value / every
      return Math.abs(q - Math.round(q)) < 1e-6
    },
    /**
     * @param {{ major: boolean, mid: boolean }} tick
     * @returns {Object}
     */
    tickClass(tick) {
      return { major: tick.major, mid: tick.mid }
    },
    /**
     * @param {number} v
     * @returns {string}
     */
    formatLabel(v) {
      return String(Math.round(v))
    },
    /**
     * @param {{ value: number }} tick
     * @returns {Object}
     */
    tickPos(tick) {
      const pos = (tick.value - this.min) * this.unitSize
      if (this.isHorizontal) return { left: `${pos}px` }
      return { top: `${pos}px` }
    },
    /**
     * @param {number} raw
     * @returns {number}
     */
    clampValue(raw) {
      const stepped = Math.round(raw / this.step) * this.step
      const precision = this.step < 1 ? 1 : 0
      const v = Number(stepped.toFixed(precision || 4))
      return Math.min(this.max, Math.max(this.min, v))
    },
    /**
     * @param {number} clientX
     * @param {number} clientY
     * @returns {void}
     */
    beginDrag(clientX, clientY) {
      this.dragging = true
      this.startPos = this.isHorizontal ? clientX : clientY
      this.startValue = this.value
    },
    /**
     * @param {number} clientX
     * @param {number} clientY
     * @returns {void}
     */
    moveDrag(clientX, clientY) {
      if (!this.dragging) return
      const cur = this.isHorizontal ? clientX : clientY
      const delta = (this.startPos - cur) / this.unitSize
      const next = this.clampValue(this.startValue + delta)
      if (next !== this.value) this.$emit('input', next)
    },
    endDrag() {
      this.dragging = false
    },
    onStart(e) {
      const t = e.touches[0]
      this.beginDrag(t.clientX, t.clientY)
    },
    onMove(e) {
      const t = e.touches[0]
      this.moveDrag(t.clientX, t.clientY)
    },
    onEnd() {
      this.endDrag()
    },
    onMouseDown(e) {
      this.beginDrag(e.clientX, e.clientY)
      const onMove = (ev) => this.moveDrag(ev.clientX, ev.clientY)
      const onUp = () => {
        this.endDrag()
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
      }
      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
    }
  }
}
</script>

<style scoped>
.ruler-picker {
  position: relative;
  overflow: hidden;
  background: #f0f9f4;
  border-radius: 8px;
  user-select: none;
  touch-action: none;
  flex-shrink: 0;
}

/* —— 纵向（身高） —— */
.ruler-picker.is-vertical {
  width: 120px;
  height: 280px;
}
.is-vertical .ruler-track {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 1px;
  height: 1px;
  will-change: transform;
}
.is-vertical .ruler-tick {
  position: absolute;
  left: 24px;
  height: 2px;
  width: 16px;
  background: #9ad9c0;
  transform: translateY(-50%);
}
.is-vertical .ruler-tick.mid {
  width: 22px;
}
.is-vertical .ruler-tick.major {
  width: 28px;
  background: #7fc9ad;
}
.is-vertical .ruler-label {
  position: absolute;
  left: 36px;
  top: 50%;
  transform: translateY(-50%);
  color: #7a8580;
  font-size: 13px;
  line-height: 1;
  white-space: nowrap;
}
.is-vertical .ruler-indicator {
  position: absolute;
  top: 50%;
  left: 16px;
  width: 48px;
  height: 4px;
  background: #2fd18a;
  border-radius: 4px;
  transform: translateY(-50%);
  z-index: 2;
  pointer-events: none;
}

/* —— 横向（体重，按截图） —— */
.ruler-picker.is-horizontal {
  width: 100%;
  height: 110px;
}
.is-horizontal .ruler-track {
  position: absolute;
  left: 50%;
  top: 0;
  width: 1px;
  height: 100%;
  will-change: transform;
}
.is-horizontal .ruler-tick {
  position: absolute;
  top: 14px;
  width: 2px;
  height: 14px;
  background: #9ad9c0;
  transform: translateX(-50%);
  border-radius: 1px;
}
.is-horizontal .ruler-tick.mid {
  height: 22px;
  background: #8fd0b6;
}
.is-horizontal .ruler-tick.major {
  height: 32px;
  width: 2px;
  background: #7fc9ad;
}
.is-horizontal .ruler-label {
  position: absolute;
  top: 40px;
  left: 50%;
  transform: translateX(-50%);
  color: #6b7570;
  font-size: 16px;
  line-height: 1;
  white-space: nowrap;
  font-weight: 400;
}
.is-horizontal .ruler-indicator {
  position: absolute;
  left: 50%;
  top: 8px;
  width: 4px;
  height: 48px;
  background: #2fd18a;
  border-radius: 4px;
  transform: translateX(-50%);
  z-index: 2;
  pointer-events: none;
}
</style>



// WEBPACK FOOTER //
// src/views/healthAssessFlow/components/RulerPicker.vue